import React, { useState, useEffect, useCallback } from 'react';
import { NominationServiceWrapper, PaginatedFeed } from '../../services/nominationServiceWrapper';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { notifyNominationComment } from '../../services/inAppNotification';
import { emailNominationComment } from '../../services/emailNotification';
import { Nomination, FeedComment } from '../../types/nomination';
import { formatNominationDate, cleanReason } from './NominationCard';
import { shareOnLinkedInWithImage } from '../../utils/linkedinShare';
import SharePreviewModal from './SharePreviewModal';
import Pagination from '../common/Pagination';
import PageHeader from '../common/PageHeader';
import './NominationFeed.css';

const AWARD_GRADIENTS: Record<string, string> = {
  'SPOT Award': 'linear-gradient(135deg, #f59e0b, #d97706)',
  'Quarterly Excellence Award': 'linear-gradient(135deg, #3b82f6, #2563eb)',
};

const AWARD_COLORS: Record<string, string> = {
  'SPOT Award': '#f59e0b',
  'Quarterly Excellence Award': '#3b82f6',
};

const NominationFeed: React.FC = () => {
  const { user } = useAuth();
  const { refresh } = useNotifications();
  const [feed, setFeed] = useState<PaginatedFeed>({ items: [], total: 0, page: 1, totalPages: 1 });
  const [commentInputs, setCommentInputs] = useState<Record<string, string>>({});
  const [expandedComments, setExpandedComments] = useState<Record<string, boolean>>({});
  const [submittingComment, setSubmittingComment] = useState<string | null>(null);
  const [shareModalNomination, setShareModalNomination] = useState<Nomination | null>(null);

  const loadFeed = useCallback(async (page: number) => {
    const result = await NominationServiceWrapper.getFeed(page);
    // Comments are already included in the nomination from GraphQL - no need to fetch separately
    // This was causing duplicate comments
    setFeed(result);
  }, []);

  useEffect(() => { loadFeed(1); }, [loadFeed]);

  const handleAddComment = async (nominationId: string) => {
    const content = commentInputs[nominationId]?.trim();
    if (!content || !user || submittingComment === nominationId) return; // Guard against double submit
    setSubmittingComment(nominationId);
    setCommentInputs(prev => ({ ...prev, [nominationId]: '' })); // Clear immediately to prevent double submit
    const result = await NominationServiceWrapper.createFeedComment(
      nominationId, user.email, user.name, content
    );
    if (result.success) {
      // Notify the nominator about the comment
      const nomination = feed.items.find(n => n.id === nominationId);
      if (nomination && nomination.nominatorId !== user.email) {
        notifyNominationComment(nomination.nominatorId, user.name, nomination.nomineeName, nominationId);
        emailNominationComment(nomination.nominatorId, user.name, nomination.nomineeName, nominationId);
        refresh();
      }
      loadFeed(feed.page);
    } else {
      // Restore input on failure
      setCommentInputs(prev => ({ ...prev, [nominationId]: content }));
    }
    setSubmittingComment(null);
  };

  const toggleComments = (id: string) => {
    setExpandedComments(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleLinkedInShare = (nomination: Nomination) => {
    setShareModalNomination(nomination);
  };

  const handleShareConfirm = (imageUrl: string, postText: string) => {
    shareOnLinkedInWithImage(imageUrl, postText);
    setShareModalNomination(null);
  };

  const getInitials = (name: string) => {
    const parts = name.trim().split(' ');
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : name.substring(0, 2).toUpperCase();
  };

  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return formatNominationDate(dateStr);
  };

  return (
    <div className="feed-page">
      <PageHeader
        icon={<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>}
        title="Award Recognitions"
        subtitle="Celebrating outstanding contributions across the team"
        badge={`${feed.total} published`}
        gradient="linear-gradient(135deg, #f59e0b 0%, #d97706 50%, #b45309 100%)"
        accentColor="#f59e0b"
      />

      <div className="feed-list">
        {feed.items.length === 0 ? (
          <div className="feed-empty">
            <div className="feed-empty-icon">🏆</div>
            <h3>No Published Recognitions Yet</h3>
            <p>Published award nominations will appear here for everyone to celebrate.</p>
          </div>
        ) : (
          feed.items.map((nom: Nomination) => {
            const comments = nom.feedComments || [];
            const isExpanded = expandedComments[nom.id];
            const visibleComments = isExpanded ? comments : comments.slice(0, 2);
            const awardGradient = AWARD_GRADIENTS[nom.awardType] || 'linear-gradient(135deg, #6b7280, #4b5563)';
            const awardColor = AWARD_COLORS[nom.awardType] || '#6b7280';

            return (
              <div className="feed-post" key={nom.id} style={{ borderLeftColor: awardColor }}>
                {/* Timeline dot */}
                <div className="feed-timeline-dot" style={{ background: awardGradient }}>★</div>

                {/* Post author row */}
                <div className="feed-post-author">
                  <div className="feed-author-avatar" style={{ background: awardGradient }}>
                    {getInitials(nom.nominatorName)}
                  </div>
                  <div className="feed-author-info">
                    <span className="feed-author-name">{nom.nominatorName}</span>
                    <span className="feed-author-meta">
                      recognized <strong>{nom.nomineeName}</strong> · {timeAgo(nom.publishedAt || nom.createdAt)}
                    </span>
                  </div>
                  <div className="feed-award-pill" style={{ background: `${awardColor}12`, color: awardColor, borderColor: `${awardColor}25` }}>
                    ★ {nom.awardType}
                  </div>
                </div>

                {/* Large nominee showcase */}
                <div className="feed-nominee-showcase" style={{ background: `${awardColor}08` }}>
                  {nom.nomineePhotoUrl ? (
                    <img src={nom.nomineePhotoUrl} alt={nom.nomineeName} className="feed-nominee-img" />
                  ) : (
                    <div className="feed-nominee-big-avatar" style={{ background: awardGradient }}>
                      {getInitials(nom.nomineeName)}
                    </div>
                  )}
                  <div className="feed-nominee-info">
                    <span className="feed-nominee-name">{nom.nomineeName}</span>
                    <span className="feed-nominee-tag" style={{ color: awardColor }}>Award Recipient</span>
                  </div>
                </div>

                {/* Reason */}
                <div className="feed-post-content">
                  <p className="feed-reason">{cleanReason(nom.reason)}</p>
                  {nom.supportingDetails && (
                    <p className="feed-details">{cleanReason(nom.supportingDetails)}</p>
                  )}
                </div>

                {/* Engagement */}
                <div className="feed-post-engagement">
                  <button className="feed-engage-btn" onClick={() => toggleComments(nom.id)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                    </svg>
                    {comments.length > 0 ? `${comments.length} Comment${comments.length !== 1 ? 's' : ''}` : 'Comment'}
                  </button>
                  <button className="feed-linkedin-btn" onClick={() => handleLinkedInShare(nom)} title="Share on LinkedIn">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                    <span>Share on LinkedIn</span>
                  </button>
                  <span className="feed-post-date">
                    {formatNominationDate(nom.publishedAt || nom.createdAt)}
                  </span>
                </div>

                {/* Comments */}
                <div className="feed-comments">
                  {visibleComments.map((c: FeedComment) => (
                    <div className="feed-cmt" key={c.id}>
                      <div className="feed-cmt-avatar">{getInitials(c.authorName)}</div>
                      <div className="feed-cmt-body">
                        <span className="feed-cmt-name">{c.authorName}</span>
                        <span className="feed-cmt-time">{timeAgo(c.createdAt)}</span>
                        <p className="feed-cmt-text">{c.content}</p>
                      </div>
                    </div>
                  ))}
                  {comments.length > 2 && !isExpanded && (
                    <button className="feed-cmt-more" onClick={() => toggleComments(nom.id)}>
                      View all {comments.length} comments
                    </button>
                  )}
                  <div className="feed-cmt-input-row">
                    <div className="feed-cmt-avatar">{user ? getInitials(user.name) : '?'}</div>
                    <input
                      type="text"
                      className="feed-cmt-input"
                      placeholder="Add a comment..."
                      value={commentInputs[nom.id] || ''}
                      onChange={(e) => setCommentInputs(prev => ({ ...prev, [nom.id]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleAddComment(nom.id); }}
                      disabled={submittingComment === nom.id}
                    />
                    <button
                      className="feed-cmt-send"
                      onClick={() => handleAddComment(nom.id)}
                      disabled={!commentInputs[nom.id]?.trim() || submittingComment === nom.id}
                      aria-label="Send comment"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="22" y1="2" x2="11" y2="13"/>
                        <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {feed.totalPages > 1 && (
        <Pagination
          currentPage={feed.page} totalItems={feed.total} itemsPerPage={10}
          hasNextPage={feed.page < feed.totalPages} hasPreviousPage={feed.page > 1}
          onPageChange={(p) => loadFeed(p)}
          onNextPage={() => loadFeed(feed.page + 1)}
          onPreviousPage={() => loadFeed(feed.page - 1)}
        />
      )}

      {/* Share Preview Modal */}
      {shareModalNomination && (
        <SharePreviewModal
          nomination={shareModalNomination}
          onClose={() => setShareModalNomination(null)}
          onShare={handleShareConfirm}
        />
      )}
    </div>
  );
};

export default NominationFeed;
